export type BillingCurrency = 'aud' | 'usd' | 'gbp' | 'eur';

type PlanType = 'starter' | 'plus' | 'pro';

const REGION_CURRENCIES: Record<string, BillingCurrency> = {
  us: 'usd',
  ca: 'usd',
  uk: 'gbp',
  eu: 'eur',
};

const CURRENCY_DISPLAY: Record<BillingCurrency, { code: string; symbol: string }> = {
  aud: { code: 'AUD', symbol: '$' },
  usd: { code: 'USD', symbol: '$' },
  gbp: { code: 'GBP', symbol: '£' },
  eur: { code: 'EUR', symbol: '€' },
};

const STARTING_PRICES: Record<BillingCurrency, Record<PlanType, number>> = {
  aud: { starter: 19, plus: 49, pro: 99 },
  usd: { starter: 15, plus: 39, pro: 75 },
  gbp: { starter: 15, plus: 29, pro: 59 },
  eur: { starter: 15, plus: 35, pro: 65 },
};

const MONTHLY_PRICES_BY_AUD: Record<number, Record<Exclude<BillingCurrency, 'aud'>, number>> = {
  19: { usd: 15, gbp: 15, eur: 15 },
  29: { usd: 25, gbp: 19, eur: 19 },
  39: { usd: 29, gbp: 25, eur: 29 },
  49: { usd: 39, gbp: 29, eur: 35 },
  59: { usd: 45, gbp: 35, eur: 39 },
  69: { usd: 55, gbp: 39, eur: 45 },
  89: { usd: 65, gbp: 50, eur: 59 },
  99: { usd: 75, gbp: 59, eur: 65 },
  109: { usd: 80, gbp: 65, eur: 75 },
  129: { usd: 95, gbp: 75, eur: 85 },
  149: { usd: 109, gbp: 85, eur: 99 },
  169: { usd: 125, gbp: 95, eur: 109 },
  189: { usd: 139, gbp: 109, eur: 125 },
  319: { usd: 235, gbp: 179, eur: 209 },
  489: { usd: 359, gbp: 275, eur: 319 },
  589: { usd: 430, gbp: 329, eur: 380 },
  689: { usd: 505, gbp: 385, eur: 445 },
};

const primaryRegion = (regionCode?: string | null) =>
  (regionCode ?? '')
    .split(',')[0]
    .trim()
    .toLowerCase();

export const isBillingCurrency = (value?: string | null): value is BillingCurrency =>
  !!value && value in CURRENCY_DISPLAY;

export const getBillingCurrency = (regionCode?: string | null): BillingCurrency =>
  REGION_CURRENCIES[primaryRegion(regionCode)] ?? 'aud';

export const getCurrencyDisplay = (currency: BillingCurrency) => CURRENCY_DISPLAY[currency];

export const getStartingPrice = (currency: BillingCurrency, planType: PlanType) => STARTING_PRICES[currency][planType];

export const getDisplayPrice = (
  audAmount: number,
  currency: BillingCurrency
): { amount: number; code: string; symbol: string } => {
  const localAmount = currency === 'aud' ? audAmount : MONTHLY_PRICES_BY_AUD[audAmount]?.[currency];
  return localAmount === undefined
    ? { amount: audAmount, ...CURRENCY_DISPLAY.aud }
    : { amount: localAmount, ...CURRENCY_DISPLAY[currency] };
};

export const getTaxLabel = (regionCode?: string | null): string | undefined => {
  const region = primaryRegion(regionCode);
  if (region === 'au') return 'ex.GST';
  if (region === 'uk' || region === 'eu') return 'ex.VAT';
  return undefined;
};
